import { Box, Text } from "../../../../../../mads";
import { useI18n } from "../../../../../../shared/I18n";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";
import {
  type LocationDisplay,
  LocationDisplayType,
  useLocationDisplayFromContext,
} from "../../../../utils/locationUtils";
import { JobTitle } from "../JobTitle/JobTitle";
import { TextLink } from "../TextLink/TextLink";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React from "react";
import { StyleSheet } from "react-native";

export function getCompactLocationText(
  locationDisplay: LocationDisplay,
  streetAddress?: string,
  remoteWorkText?: string,
): string | undefined {
  switch (locationDisplay.type) {
    case LocationDisplayType.LOCATION_WITH_REMOTE:
    case LocationDisplayType.ADDRESS_WITH_REMOTE:
      return [locationDisplay.resolvedLocation, remoteWorkText].filter(Boolean).join(" • ");
    case LocationDisplayType.STREET_ADDRESS:
      return streetAddress;
    case LocationDisplayType.LOCATION_ONLY:
      return locationDisplay.resolvedLocation;
    case LocationDisplayType.REMOTE_ONLY:
      return remoteWorkText;
    case LocationDisplayType.NONE:
      return undefined;
  }
}

export function DesktopEmbeddedCompactHeader() {
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const locationDisplay = useLocationDisplayFromContext({ allowRemoteWithoutInline: true });
  const i18n = useI18n();

  if (!viewjobOneGraphModel) {
    return null;
  }

  const { job, employer, compensation } = viewjobOneGraphModel;
  const companyName = employer?.companyName;
  const companyOverviewLink = employer?.companyOverviewLink;
  const salaryText = compensation?.salaryInfoModel?.salaryText;
  const locationText = getCompactLocationText(
    locationDisplay,
    job.location?.formattedStreetAddress,
    job.remoteWorkModel?.text,
  );

  return (
    <Box testID="desktop-embedded-compact-header" sx={styles.container}>
      <JobTitle
        compact
        ellipsizeMode="tail"
        jobTitle={job.jobTitle || ""}
        level={4}
        numberOfLines={1}
        testID="vj-job-title-compact"
      />
      <Box sx={styles.metadataRow}>
        {companyName &&
          (companyOverviewLink ? (
            <TextLink
              aria-label={`${companyName} (${i18n.gettext("opens in a new tab")})`}
              inline
              level={2}
              sx={styles.lineText}
              targetUrl={companyOverviewLink}
              variant="neutral"
            >
              {companyName}
            </TextLink>
          ) : (
            <Text level={2} sx={styles.lineText}>
              {companyName}
            </Text>
          ))}
        {companyName && locationText && (
          <Text ariaHidden level={2} sx={styles.separator}>
            •
          </Text>
        )}
        {locationText && (
          <Text level={2} sx={styles.lineText}>
            {locationText}
          </Text>
        )}
        {companyName && locationText && !!salaryText && (
          <Text ariaHidden level={2} sx={styles.separator}>
            •
          </Text>
        )}
        {!!salaryText && (
          <Text level={2} sx={styles.lineText}>
            {salaryText}
          </Text>
        )}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    minWidth: 0,
  },
  metadataRow: {
    alignItems: "center",
    columnGap: spacing.s2,
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 0,
  },
  lineText: {
    color: colors.foreground,
    padding: 0,
  },
  separator: {
    color: colors.foregroundWeak,
  },
});
